import { z } from "zod"

export const DataTypeEnum = {
  STRING: `STRING`,
  CHOICE: `CHOICE`,
  BOOLEAN: `BOOLEAN`,
  DATE: `DATE`,
  DECIMAL: `DECIMAL`,
  INTEGER: `INTEGER`,
  FORMULA: `FORMULA`,
  DEMAND: `DEMAND`,
} as const

const dataTypeEnumSchema = z.nativeEnum(DataTypeEnum)

export type DataTypeEnumType = z.infer<typeof dataTypeEnumSchema>
/**
 * The PropertyData object is used both as an input object,
 * as part of the propertyInputs list, and also as an
 * output object when returned in the assumptions list.
 * It has the following data structure:
 * https://www.switchsolar.io/api-reference/account-api/account-cost-calculation/#property-data
 */
export const propertyDataSchema = z.object({
  /**
   * The key name of the property associated with this input.
   * The most common ones are "consumption" (which is the kWh for the period)
   * and "demand" (kW), but it can also be applicability
   * properties like "cityLimits" or "hasElectricVehicle".
   */
  keyName: z.string(),

  /**
   * A display-friendly name for this property.
   */
  displayName: z.string().optional().nullable(),

  /**
   * A text description of this property.
   */
  description: z.string().optional().nullable(),

  /**
   * The start of the period where this property is applicable.
   * Default value is the start of the calculation.
   */
  fromDateTime: z.string().optional().nullable(),

  /**
   * The end of the period where this property is applicable.
   * Default value is the end of the calculation.
   */
  toDateTime: z.string().optional().nullable(),

  /**
   * The type of this property.
   * Possible values are: "STRING", "CHOICE", "BOOLEAN",
   * "DATE", "DECIMAL", "INTEGER", and "FORMULA".
   */
  dataType: dataTypeEnumSchema.optional().nullable(),

  /**
   * For applicable properties (like electricity data),
   * scales the inputs by the value provided.
   */
  dataFactor: z.number().optional().nullable(),

  /**
   * The value to use for this property.
   * Can be input as the native type, but when populated
   * will be returned as a string.
   */
  dataValue: z.string().optional().nullable(),

  /**
   * Used for properties which have many values,
   * such as consumption time series data.
   */
  dataSeries: z.array(z.number()).optional().nullable(),

  /**
   * Used with dataSeries to specify the time span
   * corresponding to each value in the dataSeries.
   */
  duration: z.number().optional().nullable(),

  /**
   * Where applicable, this is the unit of the value.
   * Most common units are "kWh" (for keys of consumption)
   * and "kW" (for keys of demand).
   */
  unit: z.string().optional().nullable(),

  /**
   * Estimated accuracy of the property value.
   * Properties that you set explicitly will have an accuracy value of 100.
   */
  accuracy: z.number().min(0).max(100).optional().nullable(),

  // Don't know what this is exactly
  propertyValue: z.string().optional().nullable(),
  isDefault: z.boolean().optional(),
  operator: z.string().optional().nullable(),
})

export type PropertyData = z.infer<typeof propertyDataSchema>
